import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Camera, Lock, LogOut, Users, Plus, Trash2, Edit3, Images, DollarSign, Shield, ArrowLeft, Key, Clock, RefreshCw, Check, X, Copy, QrCode, Package, Download, FileText, AlertTriangle, Eye, MessageCircle } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { useApp } from '../context/AppContext';
import { useToast } from '../hooks/use-toast';
import { adminAPI } from '../lib/api';
import { buildPixPayload } from '../lib/pix';

function expirationInfo(iso) {
  if (!iso) return { label: '—', expired: false, critical: false };
  try {
    const d = new Date(iso);
    const ms = d.getTime() - Date.now();
    if (ms <= 0) return { label: 'Expirado', expired: true, critical: true };
    const days = Math.floor(ms / 86400000);
    if (days === 0) {
      const hours = Math.floor(ms / 3600000);
      return { label: `${hours}h restantes`, expired: false, critical: true };
    }
    return { label: `${days} dia${days !== 1 ? 's' : ''}`, expired: false, critical: days <= 3 };
  } catch { return { label: '—', expired: false, critical: false }; }
}

export default function Admin() {
  const { adminAuthed, loginAdmin } = useApp();
  const [pwd, setPwd] = useState('');
  const { toast } = useToast();

  const submit = async (e) => {
    e.preventDefault();
    const res = await loginAdmin(pwd);
    if (res.ok) toast({ title: 'Acesso liberado', description: 'Bem-vindo, administrador.' });
    else toast({ title: res.message || 'Senha incorreta' });
  };

  if (!adminAuthed) {
    return (
      <div className="grain min-h-screen flex flex-col">
        <div className="max-w-6xl mx-auto w-full px-6 py-6">
          <Link to="/" className="inline-flex items-center gap-2 text-[#a89e87] hover:text-[#e6dcc7] text-sm">
            <ArrowLeft size={16} /> Voltar
          </Link>
        </div>
        <div className="flex-1 flex items-center justify-center px-6 pb-16">
          <form onSubmit={submit} className="w-full max-w-sm">
            <div className="flex flex-col items-center mb-8">
              <span className="brand-mark mb-3"><Shield size={22} strokeWidth={2.2} /></span>
              <h1 className="font-serif text-3xl text-[#f2ecdf]">Painel do Administrador</h1>
              <p className="text-sm text-[#a89e87] mt-2">Acesso restrito.</p>
            </div>
            <div className="card-glow rounded-2xl p-6 md:p-8">
              <label className="block">
                <span className="block text-xs uppercase tracking-wider text-[#a89e87] mb-1.5">Senha</span>
                <div className="relative">
                  <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#7d735e]" />
                  <input type="password" value={pwd} onChange={e => setPwd(e.target.value)} autoFocus
                    className="w-full bg-[#12100b] border border-[#2a2318] focus:border-[#d6af6c] focus:outline-none rounded-md pl-9 pr-3 py-2.5 text-sm text-[#f2ecdf]" />
                </div>
              </label>
              <button type="submit" className="btn-gold w-full py-3 rounded-md font-medium text-sm mt-4">Entrar</button>
              <p className="text-[11px] text-[#7d735e] mt-4 text-center">Solicite a senha ao administrador do sistema.</p>
            </div>
          </form>
        </div>
      </div>
    );
  }

  return <AdminDashboard />;
}

function AdminDashboard() {
  const { users, logoutAdmin } = useApp();
  const navigate = useNavigate();
  const [tab, setTab] = useState('users');
  const [allAlbums, setAllAlbums] = useState([]);

  React.useEffect(() => {
    adminAPI.listAllAlbums().then(setAllAlbums).catch(() => setAllAlbums([]));
  }, []);

  const totalRevenue = allAlbums.reduce((s, a) => s + (a.revenue || 0), 0);
  const totalSales = allAlbums.reduce((s, a) => s + (a.sales || 0), 0);

  return (
    <div className="grain min-h-screen">
      <header className="border-b border-[#1c1710]">
        <div className="max-w-6xl mx-auto px-6 md:px-8 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/" className="text-[#a89e87] hover:text-[#e6dcc7] inline-flex items-center gap-1.5 text-sm">
              <ArrowLeft size={16} /> Início
            </Link>
            <div className="h-5 w-px bg-[#2a2318]" />
            <Link to="/" className="flex items-center gap-2.5">
              <span className="brand-mark"><Camera size={16} strokeWidth={2.2} /></span>
              <span className="font-serif text-[20px] text-[#f2ecdf]">AXSELL <span className="gold-text text-sm ml-1">Admin</span></span>
            </Link>
          </div>
          <button onClick={async () => { await logoutAdmin(); navigate('/'); }} className="btn-outline-dark px-3 py-2 rounded-md text-sm inline-flex items-center gap-2">
            <LogOut size={15} /> Sair
          </button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 md:px-8 py-10">
        <div className="mb-8">
          <div className="text-xs uppercase tracking-[0.14em] text-[#a89e87]">Administração</div>
          <h1 className="font-serif text-4xl md:text-5xl text-[#f2ecdf] mt-1">Painel de controle</h1>
        </div>

        <div className="grid sm:grid-cols-3 gap-4 mb-10">
          <StatBox label="Usuários" value={users.length} icon={Users} />
          <StatBox label="Álbuns publicados" value={allAlbums.length} icon={Images} />
          <StatBox label="Receita total" value={`R$ ${totalRevenue.toFixed(2)}`} icon={DollarSign} sub={`${totalSales} vendas`} />
        </div>

        <div className="flex items-center gap-2 border-b border-[#1c1710] mb-6 overflow-x-auto">
          <TabBtn active={tab === 'users'} onClick={() => setTab('users')}>Usuários</TabBtn>
          <TabBtn active={tab === 'albums'} onClick={() => setTab('albums')}>Álbuns</TabBtn>
          <TabBtn active={tab === 'plan'} onClick={() => setTab('plan')}>Plano</TabBtn>
          <TabBtn active={tab === 'downloads'} onClick={() => setTab('downloads')}>Downloads</TabBtn>
          <TabBtn active={tab === 'audit'} onClick={() => setTab('audit')}>Logs</TabBtn>
          <TabBtn active={tab === 'security'} onClick={() => setTab('security')}>Segurança</TabBtn>
        </div>

        {tab === 'users' && <UsersTab />}
        {tab === 'albums' && <AlbumsTab albums={allAlbums} users={users} reload={() => adminAPI.listAllAlbums().then(setAllAlbums)} />}
        {tab === 'plan' && <PlanTab />}
        {tab === 'downloads' && <DownloadsTab />}
        {tab === 'audit' && <AuditTab />}
        {tab === 'security' && <SecurityTab />}
      </main>
    </div>
  );
}

function TabBtn({ active, onClick, children }) {
  return (
    <button onClick={onClick}
      className={`px-4 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px ${
        active ? 'border-[#d6af6c] text-[#f2ecdf]' : 'border-transparent text-[#a89e87] hover:text-[#e6dcc7]'
      }`}>
      {children}
    </button>
  );
}

function StatBox({ label, value, icon: Icon, sub }) {
  return (
    <div className="card-glow rounded-xl p-5">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-md bg-[#1a140c] flex items-center justify-center feature-icon">
          <Icon size={18} />
        </div>
        <div>
          <div className="text-xs text-[#a89e87]">{label}</div>
          <div className="font-serif text-2xl text-[#f2ecdf]">{value}</div>
          {sub && <div className="text-xs text-[#7d735e]">{sub}</div>}
        </div>
      </div>
    </div>
  );
}

function statusBadge(u) {
  const s = u.effectiveStatus || u.status || 'active';
  if (s === 'pending') return { label: 'Aguardando pagamento', color: 'bg-amber-900/40 text-amber-300 border border-amber-500/30' };
  if (s === 'blocked') return { label: 'Bloqueado', color: 'bg-red-900/40 text-red-300 border border-red-500/30' };
  if (s === 'expired') return { label: 'Expirado', color: 'bg-orange-900/40 text-orange-300 border border-orange-500/30' };
  return { label: 'Ativo', color: 'bg-emerald-900/40 text-emerald-300 border border-emerald-500/30' };
}

function paymentBadge(u) {
  const p = u.paymentStatus || (u.status === 'pending' ? 'pending' : 'paid');
  if (p === 'pending') return { label: 'Pendente', color: 'text-amber-300' };
  if (p === 'failed') return { label: 'Falhou', color: 'text-red-300' };
  return { label: 'Pago', color: 'text-emerald-300' };
}

function UsersTab() {
  const { users, createUserByAdmin, updateUserByAdmin, deleteUserByAdmin, reloadUsers } = useApp();
  const [showNew, setShowNew] = useState(false);
  const [editing, setEditing] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [pixModal, setPixModal] = useState(null); // { user, plan }
  const [filter, setFilter] = useState('all'); // all | pending | active | blocked
  const { toast } = useToast();

  const create = async (data) => {
    const res = await createUserByAdmin(data);
    if (!res.ok) { toast({ title: 'Erro', description: res.message }); return; }
    toast({ title: 'Usuário criado', description: `${data.email}` });
    setShowNew(false);
  };

  const renewUser = async (id) => {
    await adminAPI.renewUser(id, 30);
    await reloadUsers();
    toast({ title: 'Acesso renovado', description: 'Válido por mais 30 dias.' });
  };

  const activateUser = async (id) => {
    try {
      const updated = await adminAPI.activateUser(id);
      await reloadUsers();
      // Auto-open WhatsApp with the activation message so the admin only needs to
      // press "send". Uses the phone the user registered with during signup and
      // the editable template stored in the plan settings.
      const phone = (updated.phone || '').replace(/\D/g, '');
      if (phone) {
        let tpl = '';
        try {
          const plan = await adminAPI.getPlan();
          tpl = plan?.whatsappTemplate || '';
        } catch {}
        const origin = window.location.origin;
        const msg = (tpl || 'Olá {name}! Sua conta no AXSELL foi liberada. Acesse: {url}/auth\n\nE-mail: {email}')
          .replace(/\{name\}/g, updated.contactName || updated.name || '')
          .replace(/\{email\}/g, updated.email || '')
          .replace(/\{url\}/g, origin);
        window.open(`https://wa.me/${phone}?text=${encodeURIComponent(msg)}`, '_blank', 'noopener');
      }
      toast({ title: 'Acesso liberado', description: phone ? 'WhatsApp aberto — envie a mensagem para o fotógrafo.' : 'O fotógrafo já pode fazer login.' });
    } catch (e) {
      toast({ title: 'Erro', description: e?.response?.data?.detail || 'Tente novamente' });
    }
  };

  const blockUser = async (id) => {
    if (!window.confirm('Bloquear este usuário? Ele não poderá fazer login.')) return;
    await adminAPI.blockUser(id);
    await reloadUsers();
    toast({ title: 'Usuário bloqueado' });
  };

  const resendPix = async (u) => {
    try {
      const res = await adminAPI.resendPix(u.id);
      setPixModal({ user: u, plan: res.plan });
    } catch (e) {
      toast({ title: 'Erro', description: e?.response?.data?.detail || 'Tente novamente' });
    }
  };

  const pending = users.filter(u => (u.status || 'active') === 'pending');
  const filtered = filter === 'all' ? users : users.filter(u => (u.effectiveStatus || u.status || 'active') === filter);

  return (
    <div>
      <div className="flex justify-between items-center mb-4 flex-wrap gap-2">
        <div>
          <h2 className="font-serif text-2xl text-[#f2ecdf]">Usuários</h2>
          {pending.length > 0 && (
            <div className="text-xs text-amber-300 mt-1 inline-flex items-center gap-1">
              <AlertTriangle size={12} /> {pending.length} cadastro{pending.length !== 1 ? 's' : ''} aguardando liberação
            </div>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <select value={filter} onChange={e => setFilter(e.target.value)} data-testid="user-filter" className="bg-[#12100b] border border-[#2a2318] rounded-md px-3 py-2 text-sm text-[#e6dcc7]">
            <option value="all">Todos</option>
            <option value="pending">Aguardando</option>
            <option value="active">Ativos</option>
            <option value="expired">Expirados</option>
            <option value="blocked">Bloqueados</option>
          </select>
          <button onClick={() => setShowNew(true)} data-testid="new-user-btn" className="btn-gold px-4 py-2 rounded-md text-sm font-medium inline-flex items-center gap-2">
            <Plus size={15} /> Adicionar
          </button>
        </div>
      </div>

      <div className="card-glow rounded-xl overflow-hidden">
        {filtered.length === 0 && <div className="p-8 text-center text-[#a89e87]">Nenhum usuário nesse filtro.</div>}
        {filtered.map(u => {
          const expInfo = expirationInfo(u.expiresAt);
          const st = statusBadge(u);
          const pay = paymentBadge(u);
          const isPending = (u.status || 'active') === 'pending';
          return (
          <div key={u.id} data-testid={`user-row-${u.id}`} className="border-b border-[#1c1710] last:border-b-0 p-4 md:p-5 flex items-start gap-4 flex-wrap">
            <div className="flex-1 min-w-[260px]">
              <div className="flex items-center gap-2 flex-wrap">
                <div className="text-[#f2ecdf] font-medium">{u.name || '—'}</div>
                <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded ${st.color}`}>{st.label}</span>
              </div>
              {u.contactName && <div className="text-xs text-[#a89e87] mt-0.5">Responsável: {u.contactName}</div>}
              <div className="text-xs text-[#a89e87] mt-1 flex flex-wrap gap-x-3 gap-y-1">
                <span>📧 {u.email}</span>
                {u.phone && <span>📞 {u.phone}</span>}
                <span>📅 {u.createdAt ? new Date(u.createdAt).toLocaleDateString('pt-BR') : '—'}</span>
              </div>
              <div className="flex items-center gap-3 mt-1.5 text-xs">
                <span className="text-[#7d735e]">Pagamento: <span className={pay.color}>{pay.label}</span></span>
                <span className="text-[#7d735e]">•</span>
                <span className={`inline-flex items-center gap-1 ${expInfo.expired ? 'text-red-300' : expInfo.critical ? 'text-amber-300' : 'text-[#c4bba5]'}`}>
                  <Clock size={11} /> {expInfo.label}
                </span>
              </div>
            </div>
            <div className="flex items-center gap-1.5 flex-wrap">
              {isPending && (
                <button onClick={() => activateUser(u.id)} data-testid={`activate-btn-${u.id}`} className="btn-gold px-3 py-2 rounded-md text-xs font-medium inline-flex items-center gap-1.5">
                  <Check size={13} /> Liberar acesso
                </button>
              )}
              {(isPending || u.status === 'active') && u.status !== 'blocked' && (
                <button onClick={() => blockUser(u.id)} data-testid={`block-btn-${u.id}`} className="btn-outline-dark px-3 py-2 rounded-md text-xs font-medium inline-flex items-center gap-1.5 text-red-300">
                  <X size={13} /> Bloquear
                </button>
              )}
              {u.status === 'blocked' && (
                <button onClick={() => activateUser(u.id)} className="btn-outline-dark px-3 py-2 rounded-md text-xs font-medium inline-flex items-center gap-1.5 text-emerald-300">
                  <Check size={13} /> Reativar
                </button>
              )}
              {u.status !== 'blocked' && !isPending && (
                <button onClick={() => renewUser(u.id)} className="btn-outline-dark px-2.5 py-2 rounded-md text-xs" title="Renovar +30 dias">
                  <RefreshCw size={13} />
                </button>
              )}
              {isPending && (
                <button onClick={() => resendPix(u)} data-testid={`resend-pix-${u.id}`} className="btn-outline-dark px-2.5 py-2 rounded-md text-xs text-amber-300" title="Reenviar Pix">
                  <QrCode size={13} />
                </button>
              )}
              <button onClick={() => setViewing(u)} className="btn-outline-dark px-2.5 py-2 rounded-md text-xs" title="Visualizar">
                <Eye size={13} />
              </button>
              <button onClick={() => setEditing(u)} className="btn-outline-dark px-2.5 py-2 rounded-md text-xs" title="Editar">
                <Edit3 size={13} />
              </button>
              <button onClick={async () => { if (window.confirm('Excluir usuário ' + u.email + '?')) { await deleteUserByAdmin(u.id); toast({ title: 'Usuário removido' }); } }} className="btn-outline-dark px-2.5 py-2 rounded-md text-xs text-red-400" title="Excluir">
                <Trash2 size={13} />
              </button>
            </div>
          </div>
          );
        })}
      </div>

      {showNew && <UserFormModal title="Novo usuário" onClose={() => setShowNew(false)} onSave={create} />}
      {editing && <UserFormModal title="Editar usuário" user={editing} onClose={() => setEditing(null)} onSave={async (patch) => {
        await updateUserByAdmin(editing.id, patch);
        toast({ title: 'Usuário atualizado' });
        setEditing(null);
      }} />}
      {viewing && <UserViewModal user={viewing} onClose={() => setViewing(null)} />}
      {pixModal && <PixResendModal {...pixModal} onClose={() => setPixModal(null)} />}
    </div>
  );
}

function UserViewModal({ user, onClose }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} className="card-glow rounded-2xl p-6 md:p-8 max-w-md w-full">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-serif text-2xl text-[#f2ecdf]">Dados do usuário</h3>
          <button onClick={onClose}><X size={18} className="text-[#a89e87]" /></button>
        </div>
        <dl className="space-y-2.5 text-sm">
          <Row label="Empresa/Fotógrafo" value={user.name} />
          <Row label="Responsável" value={user.contactName || '—'} />
          <Row label="E-mail" value={user.email} />
          <Row label="Telefone" value={user.phone || '—'} />
          <Row label="Chave Pix" value={user.pixKey || '—'} />
          <Row label="Cadastro" value={user.createdAt ? new Date(user.createdAt).toLocaleString('pt-BR') : '—'} />
          <Row label="Expira em" value={user.expiresAt ? new Date(user.expiresAt).toLocaleString('pt-BR') : '—'} />
          <Row label="Status conta" value={user.status || 'active'} />
          <Row label="Pagamento" value={user.paymentStatus || '—'} />
        </dl>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-start justify-between gap-4 border-b border-[#1c1710] pb-2">
      <dt className="text-xs uppercase tracking-wider text-[#a89e87]">{label}</dt>
      <dd className="text-sm text-[#e6dcc7] text-right break-all">{value}</dd>
    </div>
  );
}

function PixResendModal({ user, plan, onClose }) {
  const { toast } = useToast();
  const amount = Number(plan.price || 47.90);
  const payload = plan.pixKey
    ? buildPixPayload({ key: plan.pixKey, amount, merchantName: (plan.pixName || 'AXSELL').toUpperCase(), city: (plan.pixCity || 'SAO PAULO').toUpperCase(), txid: `FL${(user.id || 'X').slice(1, 9).toUpperCase()}` })
    : '';
  const copy = () => {
    if (!payload) return;
    navigator.clipboard.writeText(payload);
    toast({ title: 'Pix copiado' });
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} className="card-glow rounded-2xl p-6 md:p-8 max-w-md w-full">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-serif text-2xl text-[#f2ecdf]">Reenviar Pix</h3>
          <button onClick={onClose}><X size={18} className="text-[#a89e87]" /></button>
        </div>
        <div className="text-center mb-4">
          <div className="text-xs text-[#a89e87]">Para: {user.name} ({user.email})</div>
          <div className="font-serif text-3xl gold-text mt-1">R$ {amount.toFixed(2).replace('.', ',')}</div>
        </div>
        {payload ? (
          <>
            <div className="bg-white rounded-xl p-4 flex justify-center mb-3">
              <QRCodeSVG value={payload} size={180} />
            </div>
            <div className="bg-[#12100b] border border-[#2a2318] rounded-md p-3 mb-3">
              <div className="text-[11px] text-[#e6dcc7] break-all font-mono max-h-24 overflow-y-auto">{payload}</div>
            </div>
            <button onClick={copy} className="btn-gold w-full py-2.5 rounded-md text-sm font-medium inline-flex items-center justify-center gap-2">
              <Copy size={14} /> Copiar código Pix
            </button>
            {user.phone && (
              <a href={`https://wa.me/${user.phone.replace(/\D/g, '')}?text=${encodeURIComponent(`Ol\u00e1! Segue o Pix do seu plano no AXSELL (R$ ${amount.toFixed(2)}):\n\n${payload}`)}`}
                 target="_blank" rel="noopener noreferrer"
                 className="btn-outline-dark w-full py-2.5 rounded-md text-sm mt-2 inline-flex items-center justify-center gap-2 text-[#25D366]">
                Enviar pelo WhatsApp
              </a>
            )}
          </>
        ) : (
          <div className="bg-amber-900/30 text-amber-200 text-sm p-3 rounded-md">
            Configure a chave Pix na aba <b>Plano</b> para gerar o QR.
          </div>
        )}
      </div>
    </div>
  );
}

function PlanTab() {
  const { toast } = useToast();
  const { reloadPublicPlan } = useApp();
  const [plan, setPlan] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    adminAPI.getPlan().then(setPlan).catch(() => setPlan({}));
  }, []);

  if (!plan) return <div className="card-glow p-8 rounded-xl text-center text-[#a89e87]">Carregando…</div>;

  const save = async (e) => {
    if (e) e.preventDefault();
    setBusy(true);
    try {
      const clean = {
        ...plan,
        price: Number(plan.price) || 0,
        days: Number(plan.days) || 30,
        benefits: Array.isArray(plan.benefits) ? plan.benefits : String(plan.benefits || '').split('\n').filter(Boolean),
      };
      const saved = await adminAPI.updatePlan(clean);
      setPlan(saved);
      // Reload the app-wide public plan so the support WhatsApp / Pix key etc.
      // are reflected in real time across every open page.
      reloadPublicPlan?.();
      toast({ title: 'Configurações salvas', description: 'A página inicial e novos cadastros já usam os novos valores.' });
    } catch (e) {
      toast({ title: 'Erro', description: e?.response?.data?.detail || 'Tente novamente' });
    }
    setBusy(false);
  };

  const set = (patch) => setPlan(p => ({ ...p, ...patch }));

  const clearPixKey = async () => {
    if (!window.confirm('Remover a chave Pix cadastrada? Nenhum novo cadastro poderá gerar QR até uma nova chave ser informada.')) return;
    const cleared = { ...plan, pixKey: '', pixName: '', pixKeyType: 'email' };
    setPlan(cleared);
    // Persist immediately
    try {
      await adminAPI.updatePlan({ ...cleared, price: Number(cleared.price) || 0, days: Number(cleared.days) || 30 });
      toast({ title: 'Chave Pix removida' });
    } catch (e) {
      toast({ title: 'Erro', description: 'Não foi possível salvar' });
    }
  };

  const keyPlaceholder = {
    cpf: '000.000.000-00',
    cnpj: '00.000.000/0000-00',
    email: 'voce@exemplo.com',
    phone: '+5511999999999',
    random: 'ex: 123e4567-e89b-...',
  }[plan.pixKeyType || 'email'];

  return (
    <div className="space-y-8">
      {/* CONFIGURAÇÕES PIX (destaque) */}
      <section>
        <div className="flex items-center gap-2 mb-4">
          <div className="w-8 h-8 rounded-md bg-cyan-900/40 border border-cyan-500/30 flex items-center justify-center">
            <MessageCircle size={16} className="text-cyan-300" />
          </div>
          <h2 className="font-serif text-2xl text-[#f2ecdf]">Mensagens automáticas</h2>
        </div>
        <div className="card-glow rounded-xl p-6 md:p-8 space-y-4" data-testid="messages-card">
          <label className="block">
            <span className="block text-xs uppercase tracking-wider text-[#a89e87] mb-1.5">
              Mensagem enviada ao aprovar cadastro (WhatsApp)
            </span>
            <textarea rows={4} value={plan.whatsappTemplate || ''} onChange={e => set({ whatsappTemplate: e.target.value })}
              data-testid="whatsapp-template"
              className="w-full bg-[#12100b] border border-[#2a2318] focus:border-[#d6af6c] focus:outline-none rounded-md px-3 py-2 text-sm text-[#f2ecdf] font-mono" />
            <span className="block text-[10px] text-[#7d735e] mt-1">
              Placeholders: {'{name}'} (nome do responsável) • {'{email}'} • {'{url}'} (endereço do app)
            </span>
          </label>
          <button onClick={save} disabled={busy} data-testid="messages-save" className="btn-gold w-full py-2.5 rounded-md font-medium text-sm disabled:opacity-60">
            {busy ? 'Salvando…' : 'Salvar mensagens'}
          </button>
        </div>
      </section>

      {/* CONFIGURAÇÕES PIX (destaque) */}
      <section>
        <div className="flex items-center gap-2 mb-4">
          <div className="w-8 h-8 rounded-md bg-emerald-900/40 border border-emerald-500/30 flex items-center justify-center">
            <QrCode size={16} className="text-emerald-300" />
          </div>
          <h2 className="font-serif text-2xl text-[#f2ecdf]">Configurações Pix</h2>
        </div>
        <div className="card-glow rounded-xl p-6 md:p-8 space-y-4" data-testid="pix-settings-card">
          <div className="grid sm:grid-cols-2 gap-4">
            <label className="block">
              <span className="block text-xs uppercase tracking-wider text-[#a89e87] mb-1.5">Tipo da chave</span>
              <select value={plan.pixKeyType || 'email'} onChange={e => set({ pixKeyType: e.target.value })}
                data-testid="pix-key-type"
                className="w-full bg-[#12100b] border border-[#2a2318] focus:border-[#d6af6c] focus:outline-none rounded-md px-3 py-2.5 text-sm text-[#f2ecdf]">
                <option value="cpf">CPF</option>
                <option value="cnpj">CNPJ</option>
                <option value="email">E-mail</option>
                <option value="phone">Telefone</option>
                <option value="random">Chave Aleatória</option>
              </select>
            </label>
            <F label="Valor padrão da assinatura (R$)" type="number" testId="pix-price" value={plan.price || 0} onChange={v => set({ price: v })} />
          </div>
          <label className="block">
            <span className="block text-xs uppercase tracking-wider text-[#a89e87] mb-1.5">Chave Pix</span>
            <div className="flex gap-2">
              <input value={plan.pixKey || ''} onChange={e => set({ pixKey: e.target.value })} placeholder={keyPlaceholder}
                data-testid="pix-key-input"
                className="flex-1 bg-[#12100b] border border-[#2a2318] focus:border-[#d6af6c] focus:outline-none rounded-md px-3 py-2.5 text-sm text-[#f2ecdf] placeholder:text-[#5c5343]" />
              {plan.pixKey && (
                <button type="button" onClick={clearPixKey} data-testid="pix-key-delete" className="btn-outline-dark px-3 rounded-md text-red-400" title="Excluir chave Pix">
                  <Trash2 size={15} />
                </button>
              )}
            </div>
          </label>
          <div className="grid sm:grid-cols-2 gap-4">
            <F label="Nome do recebedor" testId="pix-name" value={plan.pixName || ''} onChange={v => set({ pixName: v })} />
            <F label="Cidade do recebedor" testId="pix-city" value={plan.pixCity || ''} onChange={v => set({ pixCity: v })} />
          </div>
          <F label="Duração do acesso (dias)" type="number" value={plan.days || 30} onChange={v => set({ days: v })} />
          <button onClick={save} disabled={busy} data-testid="pix-save" className="btn-gold w-full py-3 rounded-md font-medium text-sm mt-2 disabled:opacity-60">
            {busy ? 'Salvando…' : 'Salvar configurações Pix'}
          </button>
          <p className="text-[11px] text-[#7d735e] text-center pt-1">
            Novos cadastros gerarão automaticamente QR Code e Pix Copia e Cola usando estas configurações.
          </p>
        </div>
      </section>

      {/* Página inicial / plano (conteúdo dinâmico) */}
      <section>
        <div className="flex items-center gap-2 mb-4">
          <div className="w-8 h-8 rounded-md bg-[#1a140c] flex items-center justify-center">
            <Package size={16} className="gold-text" />
          </div>
          <h2 className="font-serif text-2xl text-[#f2ecdf]">Conteúdo do plano e página inicial</h2>
        </div>
        <form onSubmit={save} className="card-glow rounded-xl p-6 md:p-8 space-y-4" data-testid="plan-form">
          <div className="grid sm:grid-cols-2 gap-4">
            <F label="Nome do plano" value={plan.planName || ''} onChange={v => set({ planName: v })} />
            <F label="Promoção (destaque topo)" value={plan.promo || ''} onChange={v => set({ promo: v })} />
          </div>
          <F label="Título da página inicial" value={plan.homepageTitle || ''} onChange={v => set({ homepageTitle: v })} />
          <label className="block">
            <span className="block text-xs uppercase tracking-wider text-[#a89e87] mb-1.5">Texto da página inicial</span>
            <textarea rows={2} value={plan.homepageText || ''} onChange={e => set({ homepageText: e.target.value })}
              className="w-full bg-[#12100b] border border-[#2a2318] focus:border-[#d6af6c] focus:outline-none rounded-md px-3 py-2 text-sm text-[#f2ecdf]" />
          </label>
          <label className="block">
            <span className="block text-xs uppercase tracking-wider text-[#a89e87] mb-1.5">Descrição do plano</span>
            <textarea rows={2} value={plan.description || ''} onChange={e => set({ description: e.target.value })}
              className="w-full bg-[#12100b] border border-[#2a2318] focus:border-[#d6af6c] focus:outline-none rounded-md px-3 py-2 text-sm text-[#f2ecdf]" />
          </label>
          <label className="block">
            <span className="block text-xs uppercase tracking-wider text-[#a89e87] mb-1.5">Benefícios (um por linha)</span>
            <textarea rows={5} value={(plan.benefits || []).join('\n')} onChange={e => set({ benefits: e.target.value.split('\n') })}
              className="w-full bg-[#12100b] border border-[#2a2318] focus:border-[#d6af6c] focus:outline-none rounded-md px-3 py-2 text-sm text-[#f2ecdf] font-mono" />
          </label>
          <button type="submit" disabled={busy} data-testid="plan-save" className="btn-gold w-full py-3 rounded-md font-medium text-sm mt-2 disabled:opacity-60">
            {busy ? 'Salvando…' : 'Salvar conteúdo'}
          </button>
        </form>
      </section>
    </div>
  );
}

function DownloadsTab() {
  const [data, setData] = useState({ downloads: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    adminAPI.listDownloads(500).then(d => { setData(d); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const byAlbum = {};
  for (const d of data.downloads || []) {
    const k = d.albumCode;
    if (!byAlbum[k]) byAlbum[k] = { title: d.albumTitle, client: d.albumClient, total: 0, events: [] };
    byAlbum[k].total += d.count || 0;
    byAlbum[k].events.push(d);
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="font-serif text-2xl text-[#f2ecdf]">Downloads concluídos</h2>
          <div className="text-xs text-[#a89e87] mt-1">Total de {(data.downloads || []).length} eventos • {Object.keys(byAlbum).length} álbuns</div>
        </div>
      </div>
      {loading && <div className="card-glow p-8 rounded-xl text-center text-[#a89e87]">Carregando…</div>}
      {!loading && (data.downloads || []).length === 0 && (
        <div className="card-glow p-8 rounded-xl text-center text-[#a89e87]">Nenhum download registrado ainda.</div>
      )}
      <div className="space-y-3">
        {(data.downloads || []).map(d => (
          <div key={d.id} data-testid={`download-row-${d.id}`} className="card-glow rounded-xl p-4 flex items-start gap-3 flex-wrap">
            <div className="w-10 h-10 rounded-md bg-emerald-900/40 border border-emerald-500/30 flex items-center justify-center flex-shrink-0">
              <Download size={16} className="text-emerald-300" />
            </div>
            <div className="flex-1 min-w-[240px]">
              <div className="text-[#f2ecdf] font-medium flex items-center gap-2 flex-wrap">
                {d.albumTitle || d.albumCode}
                <span className="text-[10px] uppercase tracking-wider bg-emerald-900/40 text-emerald-300 px-2 py-0.5 rounded font-semibold">Download concluído</span>
              </div>
              <div className="text-xs text-[#a89e87] mt-0.5">
                Cliente: {d.clientName || d.albumClient || '—'} • Código: {d.albumCode}
              </div>
              <div className="text-xs text-[#7d735e] mt-1 truncate max-w-full">
                Dispositivo: {d.device || '—'}
              </div>
            </div>
            <div className="text-right flex-shrink-0">
              <div className="text-[#e6dcc7] font-medium">{d.count} foto{d.count !== 1 ? 's' : ''}</div>
              <div className="text-xs text-[#7d735e]">{new Date(d.createdAt).toLocaleString('pt-BR')}</div>
            </div>
          </div>
        ))}
      </div>

      {Object.keys(byAlbum).length > 0 && (
        <div className="mt-8">
          <h3 className="font-serif text-lg text-[#f2ecdf] mb-3">Total por álbum</h3>
          <div className="card-glow rounded-xl divide-y divide-[#1c1710]">
            {Object.entries(byAlbum).map(([code, info]) => (
              <div key={code} className="flex items-center justify-between p-4">
                <div>
                  <div className="text-[#f2ecdf] text-sm font-medium">{info.title || code}</div>
                  <div className="text-xs text-[#a89e87]">{info.client || '—'} • {info.events.length} evento{info.events.length !== 1 ? 's' : ''}</div>
                </div>
                <div className="text-right">
                  <div className="gold-text font-medium">{info.total}</div>
                  <div className="text-[10px] uppercase tracking-wider text-[#7d735e]">fotos baixadas</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function AuditTab() {
  const [data, setData] = useState({ events: [] });
  const [kind, setKind] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    adminAPI.listAuditLogs(500, kind || undefined).then(d => { setData(d); setLoading(false); }).catch(() => setLoading(false));
  }, [kind]);

  const kindColor = (k) => ({
    signup: 'bg-blue-900/40 text-blue-300',
    login: 'bg-slate-900/40 text-slate-300',
    activate: 'bg-emerald-900/40 text-emerald-300',
    block: 'bg-red-900/40 text-red-300',
    'delete-user': 'bg-red-900/40 text-red-300',
    'plan-update': 'bg-purple-900/40 text-purple-300',
    download: 'bg-amber-900/40 text-amber-300',
    'admin-action': 'bg-cyan-900/40 text-cyan-300',
  }[k] || 'bg-[#1a140c] text-[#a89e87]');

  return (
    <div>
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h2 className="font-serif text-2xl text-[#f2ecdf]">Logs de auditoria</h2>
        <select value={kind} onChange={e => setKind(e.target.value)} data-testid="audit-filter" className="bg-[#12100b] border border-[#2a2318] rounded-md px-3 py-2 text-sm text-[#e6dcc7]">
          <option value="">Todos os eventos</option>
          <option value="signup">Cadastros</option>
          <option value="login">Logins</option>
          <option value="activate">Ativações</option>
          <option value="block">Bloqueios</option>
          <option value="delete-user">Exclusões</option>
          <option value="plan-update">Alterações de plano</option>
          <option value="download">Downloads</option>
          <option value="admin-action">Ações admin</option>
        </select>
      </div>
      {loading && <div className="card-glow p-8 rounded-xl text-center text-[#a89e87]">Carregando…</div>}
      {!loading && (data.events || []).length === 0 && (
        <div className="card-glow p-8 rounded-xl text-center text-[#a89e87]">Nenhum registro encontrado.</div>
      )}
      <div className="space-y-2">
        {(data.events || []).map(e => (
          <div key={e.id} className="card-glow rounded-lg p-3 flex items-center gap-3 flex-wrap">
            <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold px-2 py-1 rounded ${kindColor(e.kind)}`}>
              <FileText size={11} /> {e.kind}
            </span>
            <div className="flex-1 min-w-[200px] text-sm text-[#e6dcc7]">
              <span className="text-[#a89e87]">{e.actorName || e.actor || 'sistema'}</span>
              {e.target && <span className="text-[#7d735e]"> → {e.target}</span>}
              {e.meta && Object.keys(e.meta).length > 0 && (
                <span className="text-xs text-[#7d735e] ml-2 break-all">
                  {Object.entries(e.meta).slice(0, 3).map(([k, v]) => `${k}: ${String(v).slice(0, 40)}`).join(' • ')}
                </span>
              )}
            </div>
            <div className="text-xs text-[#7d735e] flex-shrink-0">{new Date(e.createdAt).toLocaleString('pt-BR')}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function UserFormModal({ title, user, onClose, onSave }) {
  const [f, setF] = useState({
    name: user?.name || '',
    contactName: user?.contactName || '',
    email: user?.email || '',
    phone: user?.phone || '',
    password: user?.password || '',
    pixKey: user?.pixKey || ''
  });
  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} className="card-glow rounded-2xl p-6 md:p-8 max-w-md w-full max-h-[92vh] overflow-y-auto">
        <h3 className="font-serif text-2xl text-[#f2ecdf] mb-4">{title}</h3>
        <div className="space-y-3">
          <F label="Nome da empresa/fotógrafo" value={f.name} onChange={v => setF({...f, name: v})} />
          <F label="Nome do responsável" value={f.contactName} onChange={v => setF({...f, contactName: v})} />
          <F label="E-mail" type="email" value={f.email} onChange={v => setF({...f, email: v})} />
          <F label="Telefone" value={f.phone} onChange={v => setF({...f, phone: v})} />
          <F label="Senha" type="text" value={f.password} onChange={v => setF({...f, password: v})} />
          <F label="Chave Pix (do fotógrafo)" value={f.pixKey} onChange={v => setF({...f, pixKey: v})} />
        </div>
        <div className="flex justify-end gap-2 mt-6">
          <button onClick={onClose} className="btn-outline-dark px-4 py-2 rounded-md text-sm">Cancelar</button>
          <button onClick={() => onSave(f)} className="btn-gold px-4 py-2 rounded-md text-sm font-medium">Salvar</button>
        </div>
      </div>
    </div>
  );
}

function F({ label, type = 'text', value, onChange, testId }) {
  return (
    <label className="block">
      <span className="block text-xs uppercase tracking-wider text-[#a89e87] mb-1.5">{label}</span>
      <input type={type} value={value} onChange={e => onChange(e.target.value)}
        data-testid={testId}
        className="w-full bg-[#12100b] border border-[#2a2318] focus:border-[#d6af6c] focus:outline-none rounded-md px-3 py-2 text-sm text-[#f2ecdf]" />
    </label>
  );
}

function AlbumsTab({ albums, users, reload }) {
  const { toast } = useToast();
  return (
    <div>
      <h2 className="font-serif text-2xl text-[#f2ecdf] mb-4">Todos os álbuns</h2>
      <div className="space-y-3">
        {albums.length === 0 && <div className="card-glow rounded-xl p-8 text-center text-[#a89e87]">Sem álbuns.</div>}
        {albums.map(a => {
          const owner = users.find(u => u.id === a.ownerId);
          return (
            <div key={a.code} className="card-glow rounded-xl p-5 flex items-center gap-4 flex-wrap">
              <div className="w-14 h-14 rounded-md overflow-hidden bg-[#1a140c] flex-shrink-0">
                {(a.coverUrls?.[0] || a.photos?.[0]?.url) && <img src={a.coverUrls?.[0] || a.photos[0].url} alt="" className="w-full h-full object-cover" />}
              </div>
              <div className="flex-1 min-w-[200px]">
                <div className="font-serif text-lg text-[#f2ecdf]">{a.title}</div>
                <div className="text-xs text-[#a89e87] mt-0.5">{owner?.name || 'Sem dono'} • {a.photoCount ?? (a.photos || []).length} fotos • /album/{a.code}</div>
              </div>
              <div className="text-right">
                <div className="text-xs text-[#a89e87]">Receita</div>
                <div className="gold-text font-medium">R$ {(a.revenue || 0).toFixed(2)}</div>
              </div>
              <button onClick={async () => { if (window.confirm('Excluir álbum?')) { await adminAPI.deleteAlbum(a.code); toast({ title: 'Álbum removido' }); reload(); } }} className="btn-outline-dark p-2 rounded-md text-red-400">
                <Trash2 size={15} />
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function SecurityTab() {
  const { changeAdminPassword } = useApp();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirmv, setConfirmv] = useState('');
  const { toast } = useToast();

  const change = async (e) => {
    e.preventDefault();
    if (next !== confirmv) { toast({ title: 'A confirmação não confere' }); return; }
    const res = await changeAdminPassword(current, next);
    if (!res.ok) { toast({ title: res.message || 'Erro' }); return; }
    setCurrent(''); setNext(''); setConfirmv('');
    toast({ title: 'Senha alterada com sucesso' });
  };

  return (
    <div>
      <h2 className="font-serif text-2xl text-[#f2ecdf] mb-4">Segurança</h2>
      <form onSubmit={change} className="card-glow rounded-xl p-6 md:p-8 max-w-md">
        <div className="flex items-center gap-2 mb-4">
          <Key size={18} className="gold-text" />
          <span className="font-medium text-[#f2ecdf]">Alterar senha do administrador</span>
        </div>
        <div className="space-y-3">
          <F label="Senha atual" type="password" value={current} onChange={setCurrent} />
          <F label="Nova senha" type="password" value={next} onChange={setNext} />
          <F label="Confirmar nova senha" type="password" value={confirmv} onChange={setConfirmv} />
        </div>
        <button type="submit" className="btn-gold w-full py-3 rounded-md font-medium text-sm mt-5">Salvar nova senha</button>
      </form>
    </div>
  );
}
